import { CompactUserResponse, UserId, UserResponse } from '@business/entities/account';
import { OrgId } from '@business/entities/organization';
import {
  CompactSupportTicketResponse,
  SupportTicketActionName,
  SupportTicketPriority,
  SupportTicketStatus,
  TicketAdditionalResponse,
  TicketId,
  TicketSearchParams,
} from '@business/entities/SupportTicket';
import { SupportTicketStatusResponse, SupportTicketStatusId } from '@business/entities/support_ticket_status';
import { CommonService, HTMLService } from '@business/services';
import { TicketService } from '@business/services/ticket_service';
import { Button } from '@core/components/Button';
import { FormSelect } from '@core/components/form';
import { GCColumn, GCTable } from '@core/components/GCTable';
import { formControl, formGroup, useFormConfig } from '@core/utils/form';
import { Formatter } from '@core/utils/formatter';
import { PaginatedController } from '@core/utils/paginated';
import { R } from '@core/utils/r';
import { toMoment } from '@core/utils/time';
import { openSelectOrgModal } from '@modules/common';
import { openTicketOrg } from '@modules/support/TicketDetailPage/Components/TicketOrgModal';
import React, { memo, useCallback, useMemo, useRef, useState, useEffect } from 'react';
import { IDataTableColumn } from 'react-data-table-component-footer';
import { Link } from 'react-router-dom';
import { Badge, DropdownMenu, DropdownToggle, UncontrolledDropdown, UncontrolledTooltip } from 'reactstrap';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { TableColumn } from 'react-data-table-component';
import ReactDOM from 'react-dom';

const PRESET_COLORS = [
  '#9e9e9e', '#4caf50', '#8bc34a', '#ffeb3b', '#2196f3', '#3f51b5', '#9c27b0', '#00bcd4', '#f44336',
  '#ff9800', '#e91e63', '#000000',
];

const STATUS_ICON_MAP: Record<string, { icon: string; color: string; label: string }> = {
  [SupportTicketStatus.open]: { icon: 'fa fa-circle', color: '#f1b44c', label: 'Open' },
  [SupportTicketStatus.replied]: { icon: 'fa fa-reply', color: '#556ee6', label: 'Replied' },
  [SupportTicketStatus.closed]: { icon: 'fa fa-check-circle', color: '#34c38f', label: 'Closed' },
  [SupportTicketStatus.markNotification]: { icon: 'fa fa-bell', color: '#343a40', label: 'Notification' },
  [SupportTicketStatus.markSpam]: { icon: 'fa fa-ban', color: '#f46a6a', label: 'Spam' },
};

interface CustomStatusDropdownProps {
  ticket: CompactSupportTicketResponse;
  statuses: SupportTicketStatusResponse[];
  onSelect(ticketId: TicketId, statusId: SupportTicketStatusId): void;
  onAddStatus(data: { name: string; color: string }): void;
}

const CustomStatusDropdown = memo((props: CustomStatusDropdownProps) => {
  const { ticket, statuses, onSelect, onAddStatus } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState(PRESET_COLORS[0]);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0 });

  useEffect(() => {
    if (!isOpen) {
      setShowAddForm(false);
      setNewName('');
      setNewColor(PRESET_COLORS[0]);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 2, left: rect.left });
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(event.target as Node) &&
        buttonRef.current && !buttonRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleAdd = useCallback(() => {
    if (newName.trim()) {
      onAddStatus({ name: newName.trim(), color: newColor });
      setNewName('');
      setNewColor(PRESET_COLORS[0]);
      setShowAddForm(false);
    }
  }, [newName, newColor, onAddStatus]);

  const currentStatus = ticket.supportStatus;

  const dropdownContent = isOpen ? ReactDOM.createPortal(
    <div
      ref={dropdownRef}
      style={{
        position: 'fixed',
        top: dropdownPos.top,
        left: dropdownPos.left,
        zIndex: 9999,
        background: '#fff',
        border: '1px solid #ddd',
        borderRadius: 4,
        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
        minWidth: 220,
        maxHeight: 350,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ overflowY: 'auto', maxHeight: 220, padding: '4px 0' }}>
        {statuses.map(status => (
          <div
            key={status.id}
            className="dropdown-item"
            style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px' }}
            onClick={() => {
              onSelect(ticket.id, status.id);
              setIsOpen(false);
            }}
          >
            <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: status.color, flexShrink: 0 }} />
            <span>{status.name}</span>
          </div>
        ))}
      </div>
      <div style={{ borderTop: '1px solid #eee', padding: 8 }}>
        {!showAddForm ? (
          <a className="text-primary" style={{ cursor: 'pointer', fontSize: 13 }} onClick={() => setShowAddForm(true)}>
            + Add new status
          </a>
        ) : (
          <div>
            <input
              type="text"
              className="form-control form-control-sm mb-2"
              placeholder="Status name"
              value={newName}
              onChange={e => setNewName(e.target.value)}
              autoFocus
            />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: 8 }}>
              {PRESET_COLORS.map(color => (
                <span
                  key={color}
                  onClick={() => setNewColor(color)}
                  style={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    backgroundColor: color,
                    cursor: 'pointer',
                    border: newColor === color ? '2px solid #333' : '2px solid transparent',
                    display: 'inline-block',
                  }}
                />
              ))}
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn btn-sm btn-primary" onClick={handleAdd}>Add</button>
              <button className="btn btn-sm btn-link" onClick={() => setShowAddForm(false)}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <button
        ref={buttonRef}
        className="btn btn-sm btn-outline-secondary"
        onClick={() => setIsOpen(!isOpen)}
        style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: 6, fontSize: 12 }}
      >
        {currentStatus ? (
          <>
            <span style={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: currentStatus.color, display: 'inline-block' }} />
            {currentStatus.name}
          </>
        ) : (
          <span style={{ color: '#999' }}>Set Status</span>
        )}
        <i className="fa fa-caret-down" style={{ fontSize: 10, marginLeft: 2 }}></i>
      </button>
      {dropdownContent}
    </>
  );
});

interface StatusCellProps {
  ticket: CompactSupportTicketResponse;
  statuses: SupportTicketStatusResponse[];
  onSelectStatus(ticketId: TicketId, statusId: SupportTicketStatusId): void;
  onAddStatus(data: { name: string; color: string }): void;
}

const StatusCell = memo((props: StatusCellProps) => {
  const { ticket, statuses, onSelectStatus, onAddStatus } = props;
  const statusInfo = STATUS_ICON_MAP[ticket.status] || STATUS_ICON_MAP[SupportTicketStatus.open];

  return (
    <div className="d-flex align-items-center py-1" style={{ gap: 8 }}>
      <span id={`static-status-${ticket.id}`}>
        <i
          className={statusInfo.icon}
          style={{ color: statusInfo.color, fontSize: 14, cursor: 'default' }}
        />
      </span>
      <UncontrolledTooltip placement="top" target={`static-status-${ticket.id}`}>
        {statusInfo.label}
      </UncontrolledTooltip>
      <CustomStatusDropdown
        ticket={ticket}
        statuses={statuses}
        onSelect={onSelectStatus}
        onAddStatus={onAddStatus}
      />
    </div>
  );
});

interface SubjectItemProps {
  ticket: CompactSupportTicketResponse;
}

const SubjectItem = memo((props: SubjectItemProps) => {
  const { ticket } = props;

  return (
    <div className="py-2 d-flex" style={{ flex: 1, alignItems: 'center' }}>
      <div style={{ flex: 1 }}>
        <Link to={`/support-tickets/detail/${ticket.id}`} className="text-primary">
          <h6 className="mb-1">{ticket.subject}</h6>
        </Link>
        <div className="font-size-12 pt-1 text-muted">{ticket.fromEmail}</div>
      </div>
      {ticket.tasks > 0 && (
        <span className="text-muted mr-3 font-size-12" style={{ opacity: 0.8 }}>
          <i className="fa fa-sitemap mr-2"></i>
          <span style={{ fontWeight: '600' }}>{`${ticket.completedTasks}/${ticket.tasks}`}</span>
        </span>
      )}

      <div className="d-flex flex-column align-items-end">
        <div id={`ticket-tooltip-${ticket.id}`} className="hover-pointer" style={{ padding: 5 }}>
          <i className="fa fa-eye"></i>
        </div>

        <UncontrolledTooltip placement="top" target={`ticket-tooltip-${ticket.id}`}>
          <div style={{ alignItems: 'flex-start', display: 'flex', flexDirection: 'column', textAlign: 'left' }}>
            {HTMLService.toReact(ticket.lastThreadText)}
          </div>
        </UncontrolledTooltip>
        <div>
          {!R.isEmpty(ticket.categoryLabel) && (
            <Badge color={'info'} className={`badge-soft font-size-10 px-2 py-1 mr-2`}>
              {ticket.categoryLabel}
            </Badge>
          )}
          {ticket.importId === '0' && !R.isEmpty(ticket.tenantName) && (
            <Badge color={'primary'} className={`badge-soft font-size-10 px-2 py-1`}>
              Escalated to {ticket.tenantName}
            </Badge>
          )}
        </div>
      </div>
    </div>
  );
});

export interface TicketListingTableProps {
  paginated: PaginatedController<CompactSupportTicketResponse, string, TicketSearchParams, TicketAdditionalResponse>;
  users: CompactUserResponse[] | UserResponse[];
  tickets: CompactSupportTicketResponse[];
  supportTicketStatuses: SupportTicketStatusResponse[];
  onAssignTo(id: TicketId, action: SupportTicketActionName, actionId: string): Observable<any>;
  onUpdateBulk(ids: TicketId[], status: SupportTicketStatus): Observable<any>;
  updateTicketAction(ticketId: TicketId, action: SupportTicketActionName, actionId: string): Observable<any>;
  onAddSupportStatus(data: { name: string; color: string }): void;
  initialSearchString?: string;
}

export const TicketListingTable = memo((props: TicketListingTableProps) => {
  const { tickets, users, paginated, onAssignTo, onUpdateBulk, updateTicketAction, supportTicketStatuses, onAddSupportStatus, initialSearchString } = props;
  const [selectedIds, setSelectedIds] = useState<TicketId[]>([]);
  const [toggledClearRows, setToggleClearSelectedRows] = useState(false);

  const form = useMemo(() => formGroup({ action: formControl<SupportTicketStatus>({}) }), []);

  const onSelectedRowsChange = useCallback(({ selectedRows }) => {
    const ids = (selectedRows as CompactSupportTicketResponse[]).map(t => t.id);
    setSelectedIds(ids);
    setToggleClearSelectedRows(false);
  }, []);

  const onPriorityUpdate = useCallback(
    (ticketId: TicketId, priority: SupportTicketPriority) => {
      updateTicketAction(ticketId, SupportTicketActionName.priority, priority).subscribe();
    },
    [updateTicketAction],
  );

  const onSelectSupportStatus = useCallback(
    (ticketId: TicketId, statusId: SupportTicketStatusId) => {
      updateTicketAction(ticketId, SupportTicketActionName.supportStatusId, statusId).subscribe();
    },
    [updateTicketAction],
  );

  const controls = useMemo(() => {
    return tickets.map(ticket =>
      formControl<UserId>({
        placeholder: 'Assignee',
        initialValue: ticket.assignedTo?.id,
      }),
    );
  }, [tickets]);

  const updateTicketAssignee = useCallback(
    (ticketId: TicketId, userId: UserId) => {
      onAssignTo(ticketId, SupportTicketActionName.assignedTo, userId).subscribe();
    },
    [onAssignTo],
  );

  const usersItems = useMemo(() => CommonService.selectItems(users, u => CommonService.getFullName(u), 'id'), [users]);

  const onSort = useCallback(
    (column: TableColumn<CompactSupportTicketResponse>, sortDirection: 'asc' | 'desc') => {
      paginated.patchQuery({ sortBy: column.sortField as string, sortDirection }).subscribe();
    },
    [paginated],
  );

  const columns: GCColumn<CompactSupportTicketResponse>[] = useMemo(
    () => [
      {
        id: 'priority',
        width: '50px',
        name: ' ',
        sortField: 'priority',
        sortable: true,
        cell: ticket => (
          <UncontrolledDropdown style={{ width: '100%' }}>
            <DropdownToggle tag="a" className="font-size-16 d-flex justify-content-center align-items-center">
              <i style={{ color: TicketService.getPriorityColor(ticket.priority) }} className="fa fa-flag"></i>
            </DropdownToggle>

            <DropdownMenu container="body" className="dd-menu_outside">
              <a className="dropdown-item" onClick={() => onPriorityUpdate(ticket.id, SupportTicketPriority.normal)}>
                <i style={{ color: TicketService.getPriorityColor(SupportTicketPriority.normal) }} className="fa fa-flag mr-2"></i>Normal
              </a>
              <a className="dropdown-item" onClick={() => onPriorityUpdate(ticket.id, SupportTicketPriority.major)}>
                <i style={{ color: TicketService.getPriorityColor(SupportTicketPriority.major) }} className="fa fa-flag mr-2"></i>Major
              </a>
              <a className="dropdown-item" onClick={() => onPriorityUpdate(ticket.id, SupportTicketPriority.critical)}>
                <i style={{ color: TicketService.getPriorityColor(SupportTicketPriority.critical) }} className="fa fa-flag mr-2"></i>Critical
              </a>
            </DropdownMenu>
          </UncontrolledDropdown>
        ),
      },
      {
        id: 'statusColumn',
        name: 'Status',
        width: '200px',
        cell: ticket => (
          <StatusCell
            ticket={ticket}
            statuses={supportTicketStatuses}
            onSelectStatus={onSelectSupportStatus}
            onAddStatus={onAddSupportStatus}
          />
        ),
      },
      {
        id: 'subject',
        name: 'Subject',
        minWidth: '300px',
        sortable: true,
        sortField: 'subject',
        cell: ticket => <SubjectItem ticket={ticket} />,
      },
      {
        id: 'org',
        name: 'Organizations',
        maxWidth: '200px',
        sortable: true,
        sortField: 'organization',
        cell: ticket => (
          <div className="py-2">
            {!R.isEmpty(ticket.orgName) && (
              <a className="text-primary" href={`/organizations/${ticket.orgId}`} target="_blank" rel="noopener noreferrer">
                {ticket.orgName}
              </a>
            )}
            <a
              className="text-primary"
              onClick={() => {
                ticket.orgId != '0'
                  ? openSelectOrgModal({
                      onSelect: (orgId: OrgId) => updateTicketAction(ticket.id, SupportTicketActionName.orgId, orgId),
                    })
                  : openTicketOrg({ ticketId: ticket.id, email: ticket.fromEmail, name: ticket.fromName });
              }}
            >
              {R.isEmpty(ticket.orgName) && <span>---</span>}
              <i className="bx bx-edit ml-2"></i>
            </a>
          </div>
        ),
      },

      {
        id: 'assignedTo',
        name: 'Assigned To',
        maxWidth: '205px',
        sortable: true,
        sortField: 'assignedTo',
        cell: (ticket, index) => (
          <div className="w-100">
            {!!controls[index] && (
              <FormSelect withoutGroup items={usersItems} control={controls[index]} onChange={userId => updateTicketAssignee(ticket.id, userId)} />
            )}
          </div>
        ),
      },
      {
        id: 'lastActivity',
        name: 'Last Activity',
        maxWidth: '125px',
        sortable: true,
        sortField: 'lastActivity',
        cell: ticket => (
          <div className="py-2">
            <p className="mb-2 l-h-1 ">{ticket.lastActivityTime ? toMoment(ticket.lastActivityTime).fromNow() : ''}</p>
            <div className="l-h-1 text-muted font-size-12">{Formatter.formatTime(ticket.lastActivityTime, 'MMM D')}</div>
          </div>
        ),
      },

      {
        id: 'createdAt',
        name: 'Created',
        maxWidth: '125px',
        sortable: true,
        sortField: 'createdAt',
        cell: ticket => (
          <div className="l-h-1">
            {Formatter.formatTime(ticket.createdAt, toMoment(ticket.createdAt).get('year') == toMoment().get('year') ? 'MMM D' : 'MMM D, YYYY')}
          </div>
        ),
      },
    ],
    [controls, onPriorityUpdate, onSelectSupportStatus, supportTicketStatuses, onAddSupportStatus, updateTicketAction, updateTicketAssignee, usersItems],
  );

  const statusItems = useMemo(
    () => CommonService.selectItems(TicketService.allStatuses(true), i => TicketService.getStatusTitle(i, false, true)),
    [],
  );

  const onSubmit = useCallback(
    ({ action }) => {
      return onUpdateBulk(selectedIds, action).pipe(tap(() => setToggleClearSelectedRows(true)));
    },
    [onUpdateBulk, selectedIds],
  );

  useFormConfig(form, { onSubmit });

  return (
    <div>
      <GCTable
        initialSearchString={initialSearchString}
        columns={columns}
        data={tickets}
        paginated={paginated}
        selectableRows
        selectableRowsHighlight
        onSelectedRowsChange={onSelectedRowsChange}
        clearSelectedRows={toggledClearRows}
        sortServer
        onSort={onSort}
        defaultSortField="lastActivityTime"
        defaultSortAsc={false}
        paginationPerPage={20}
        paginationRowsPerPageOptions={[20, 40, 100]}
      >
        {selectedIds.length > 0 && (
          <div style={{ flex: 1, flexDirection: 'row', display: 'flex' }}>
            <div style={{ width: 200, marginRight: 10 }}>
              <FormSelect withoutGroup control={form.controls.action} items={statusItems} />
            </div>
            <Button onClick={() => form.submit()}>GO</Button>
          </div>
        )}
      </GCTable>
    </div>
  );
});
